import React, { useState, useEffect, useRef } from "react";
import { FaRobot, FaPaperPlane, FaTimes, FaDownload, FaComments } from "react-icons/fa";
import { getFunctions, httpsCallable } from "firebase/functions";
import app from "../firebaseConfig";
import { CVData } from "./CVContent";
import { ProjectData } from "./ProjectData";
import { PortfolioData } from "../data/PortfolioData";
import { downloadChatHistory } from "../utils/ChatHistoryExporter";
import { useNavigate } from "react-router-dom"; // Navigation for "Message Laurence"

const functions = getFunctions(app);
const chatWithAI = httpsCallable(functions, "chatWithAI");

const starterPrompts = [
  { label: "Experience overview", query: "Give me an overview of Laurence's experience" },
  { label: "Key achievements", query: "What are his key achievements?" },
  { label: "Leadership & communication", query: "Tell me about Laurence's leadership, communication, and stakeholder experience" },
  { label: "Why hire him?", query: "Why should we hire Laurence?" },
  { label: "Technical skills", query: "What are his technical skills?" },
];

const AIChatBot = () => {
  const navigate = useNavigate(); // For redirecting to Contact Us page
  const [isOpen, setIsOpen] = useState(false); // Controls chat visibility
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm Laurence's AI Portfolio Assistant. Ask about Laurence's experience, skills, or background." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [aiTyping, setAiTyping] = useState(""); // Typing effect
  const [bannerOpen, setBannerOpen] = useState(false); // Consent bar visible?
  const chatRef = useRef(null);

  // Shift the floating UI up while the bottom consent bar is on screen.
  useEffect(() => {
    const handler = (e) => setBannerOpen(Boolean(e.detail?.open));
    window.addEventListener("consent-banner-visibility", handler);
    return () => window.removeEventListener("consent-banner-visibility", handler);
  }, []);

  useEffect(() => {
    if (chatRef.current) {
      //chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages, aiTyping]);

  // Speech bubble: appears once after 2.5s, fades out after 3s
  const [bubbleVisible, setBubbleVisible] = useState(false);
  const [bubbleFading, setBubbleFading] = useState(false);
  useEffect(() => {
    const showTimer = setTimeout(() => {
      setBubbleVisible(true);
      const hideTimer = setTimeout(() => {
        setBubbleFading(true);
        const removeTimer = setTimeout(() => {
          setBubbleVisible(false);
          setBubbleFading(false);
        }, 500); // fade-out duration
        return () => clearTimeout(removeTimer);
      }, 3000); // visible duration
      return () => clearTimeout(hideTimer);
    }, 2500); // delay before showing
    return () => clearTimeout(showTimer);
  }, []);


  const predefinedResponses = {
    "tell me about yourself": "Laurence is a hands-on digital systems and customer-focused operations professional with nearly four years of experience as the primary technical and digital operations contact in a live office environment. His background spans technical support, workflow improvement, bookings, reporting tools, website updates, stakeholder communication, and building live systems used by staff and community members. He is currently completing his Certificate III in IT and is targeting roles across digital operations, customer experience, junior business analysis, product support, and customer-facing technology teams.",
    "what's your greatest strength": "Laurence stays calm under pressure and works systematically through problems until they're resolved. He's managed live production systems with real users depending on them and has investigated real incidents with real operational impact.",
    "why should we hire you": "Laurence brings genuine hands-on experience across support, systems, communication, and process improvement in low-resource environments. He's reliable, methodical, customer-focused, and used to translating operational problems into practical digital solutions.",
  };

  const findClosestMatch = (userInput) => {
    const lowercaseInput = userInput.toLowerCase();
    for (let key in predefinedResponses) {
      if (lowercaseInput.includes(key.toLowerCase())) {
        return predefinedResponses[key];
      }
    }
    return null;
  };

  // âœ… AI Message Formatting (Markdown-style)
  const formatMessage = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") // Bold
      .replace(/\*(.*?)\*/g, "<em>$1</em>") // Italic
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" class="text-blue-400 underline">$1</a>') // Links
      .replace(/^(\s+)/gm, (match) => "&nbsp;".repeat(match.length)) // Preserve indentation
      .replace(/\n{2,}/g, "<br /><br />") // Double newlines = paragraph breaks
      .replace(/\n/g, "<br />"); // Single newline = line break
  };



  // âœ… Handle Message Sending
  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { role: "user", content: input, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      let aiMessageContent = findClosestMatch(input);

      if (!aiMessageContent) {
        const systemPrompt = `
          You are an AI assistant representing Laurence Klein, a digital systems and customer-focused operations professional. You are NOT Laurence — you are his AI Portfolio Assistant. Always refer to yourself as "Laurence's AI Portfolio Assistant" if asked who or what you are. Always refer to Laurence in the third person ("Laurence has...", "He has experience with...", "His background is..."). Never say "I am Laurence" or speak as if you are him.

          Your job is to help recruiters and hiring managers learn about Laurence's background, skills, and experience in a clear, professional, and honest way.

          When answering questions:
          - Always refer to Laurence in the third person.
          - Focus on digital operations, customer-facing support, workflow improvement, technical support, and stakeholder communication.
          - Speak concretely about Microsoft 365, Google Workspace, websites, bookings, reporting workflows, DNS, Windows endpoint tasks, and live system support where relevant.
          - Emphasise reliability, operational responsibility, calm problem-solving under pressure, and the ability to improve processes in low-resource environments.
          - Avoid overstating formal UX, product, or enterprise business analyst experience.
          - Keep responses professional, direct, and focused on what matters to employers in digital operations, customer experience, support, and junior business-analysis-adjacent roles.
          - Be honest about gaps (e.g. limited enterprise AD experience, no formal ITIL) â€” don't oversell.
          - If asked about a specific skill or tool, give a practical, grounded answer drawing on real experience.

        Here is Laurence's structured CV data:
        ${JSON.stringify(CVData, null, 2)}

        Here is Laurence's project data:
        ${JSON.stringify(ProjectData, null, 2)}

        Here is Laurence's extended portfolio profile â€” includes narrative, full experience with context and key achievements, projects with outcomes, personal brand, honest strengths and weaknesses, career goals, salary expectations, and availability:
        ${JSON.stringify(PortfolioData, null, 2)}

        Use all three data sources together. For behavioural or narrative questions (why IT, greatest strength, work style, salary, availability), draw primarily from PortfolioData. For specific CV details and formatting, use CVData.
        `;

        // Clean messages — Firebase function only accepts role + content
        const apiMessages = messages.map(({ role, content }) => ({ role, content }));

        const result = await chatWithAI({
          messages: [...apiMessages, { role: "user", content: input }],
          systemPrompt,
        });

        aiMessageContent = result.data.reply;
      }

      setMessages((prev) => [...prev, { role: "assistant", content: aiMessageContent, timestamp: new Date().toISOString() }]);
      setAiTyping("");
    } catch (error) {
      console.error("OpenAI API Error:", error);
      setMessages((prev) => [...prev, { role: "assistant", content: "Something went wrong. Please try again." }]);
      setAiTyping("");
    }

    setLoading(false);
  };




  // âœ… Handle Enter Key Press
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      sendMessage();
    }
  };

  // Scroll to contact section on home page
  const goToContact = () => {
    setIsOpen(false);
    navigate("/");
    setTimeout(() => {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    }, 150);
  };

  return (
    <>
      {/* Floating Action Button (FAB) */}
      <div
        className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-[9998] flex flex-col items-end gap-2 transition-all duration-300"
        style={{ bottom: bannerOpen ? "5.5rem" : undefined }}
      >
        {/* Speech bubble — shown once on load, then fades out */}
        {!isOpen && bubbleVisible && (
          <div
            className="relative bg-white border border-gray-200 text-gray-700 text-xs rounded-xl px-3 py-2 shadow-md whitespace-nowrap text-right transition-opacity duration-500"
            style={{ opacity: bubbleFading ? 0 : 1 }}
          >
            Got questions about Laurence? I can help!
            {/* Tail */}
            <span className="absolute bottom-[-6px] right-4 w-3 h-3 bg-white border-r border-b border-gray-200 rotate-45 block" />
          </div>
        )}
        <button
          onClick={() => { setIsOpen(!isOpen); setBubbleVisible(false); }}
          className="bg-blue-600 border border-blue-500 text-white px-4 py-2.5 rounded-full shadow-lg hover:bg-blue-700 transition flex items-center gap-2 text-sm font-semibold"
        >
          {isOpen ? <FaTimes size={14} /> : <><FaRobot size={14} /><span>Ask AI</span></>}
        </button>
      </div>

      {/* ðŸ’¬ Chat Box */}
      {isOpen && (
        <div
          className="fixed left-4 right-4 md:left-auto md:right-6 md:w-[26rem] bg-slate-950 text-white p-3 md:p-4 rounded-xl md:rounded-2xl shadow-2xl border border-slate-800 z-[9999] transition-all duration-300"
          style={{ bottom: bannerOpen ? "10rem" : "5rem" }}
        >
          <div className="flex justify-between items-center border-b border-slate-800 pb-2 md:pb-3">
            <h2 className="text-base md:text-lg font-semibold flex items-center gap-2 pr-3">
              <FaComments /> Laurence's AI Portfolio Assistant
            </h2>
            <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-white transition">
              <FaTimes />
            </button>
          </div>

          {/* Chat Messages */}
          <div ref={chatRef} className="h-48 md:h-60 overflow-y-auto border-b border-slate-800 py-3 space-y-2">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`p-3 rounded-xl max-w-[90%] border ${msg.role === "user" ? "ml-auto bg-blue-600 border-blue-500 text-white" : "bg-slate-900 border-slate-700 text-slate-100 shadow-sm"
                  }`}
                dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} // Format AI messages
              ></div>
            ))}
            {/* Loading indicator */}
            {loading && !aiTyping && (
              <div className="p-3 rounded-xl bg-slate-900 border border-slate-700 text-slate-300 text-sm flex items-center gap-2">
                <span>Responding</span>
                <span className="flex gap-1 items-center">
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </span>
              </div>
            )}
            {/* ðŸ”¥ AI Typing Effect */}
            {aiTyping && <div className="p-3 rounded-xl bg-slate-900 border border-slate-700 text-slate-200">{aiTyping}</div>}
          </div>

          {/* Starter prompts — horizontal scrollable chips */}
          {messages.length === 1 && (
            <div className="pt-3 pb-2">
              <p className="mb-2 text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-slate-500">Quick prompts</p>
              <div className="flex gap-2 overflow-x-auto md:flex-wrap md:overflow-visible pb-1 scrollbar-none" style={{ scrollbarWidth: "none" }}>
                {starterPrompts.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setInput(prompt.query);
                      const userMessage = { role: "user", content: prompt.query, timestamp: new Date().toISOString() };
                      setMessages((prev) => [...prev, userMessage]);
                      setLoading(true);
                      (async () => {
                        try {
                          const systemPrompt = `You are an AI assistant representing Laurence Klein, a digital systems and customer-focused operations professional. Always refer to Laurence in the third person. Keep responses professional and concise.\n\nHere is Laurence's full portfolio data:\n${JSON.stringify(PortfolioData, null, 2)}`;
                          const result = await chatWithAI({
                            messages: [{ role: "user", content: prompt.query }],
                            systemPrompt,
                          });
                          setMessages((prev) => [...prev, { role: "assistant", content: result.data.reply, timestamp: new Date().toISOString() }]);
                        } catch {
                          setMessages((prev) => [...prev, { role: "assistant", content: "Something went wrong. Please try again." }]);
                        }
                        setLoading(false);
                        setInput("");
                      })();
                    }}
                    className="flex-shrink-0 text-[11px] md:text-xs bg-slate-900/90 hover:bg-slate-800 text-slate-200 hover:text-white px-2.5 md:px-3 py-1.5 rounded-full transition border border-slate-600 whitespace-nowrap shadow-sm"
                  >
                    {prompt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input & Send Button */}
          <div className="flex mt-3 gap-2 items-center">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 min-w-0 p-2.5 bg-slate-800 border border-slate-700 text-white rounded-xl placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ask about Laurence's experience..."
              disabled={loading}
            />
            <button
              onClick={sendMessage}
              className="shrink-0 p-3 bg-blue-600 rounded-xl hover:bg-blue-500 transition disabled:bg-slate-700"
              disabled={loading}
            >
              <FaPaperPlane />
            </button>
          </div>

          {/* âœ… Download Chat Button (if AI has responded) */}
          {messages.length > 2 && (
            <button
              onClick={() => downloadChatHistory(messages)}
              className="mt-3 bg-slate-800 border border-slate-700 text-white w-full p-2.5 rounded-xl flex items-center justify-center gap-2 hover:bg-slate-700 transition"
            >
              <FaDownload /> Download Chat
            </button>
          )}

          {/* âœ… "Message Laurence" Link to Contact Page */}
          <button
            onClick={goToContact}
            className="mt-3 text-slate-400 hover:text-white hover:underline text-sm flex justify-center transition"
          >
            Message Laurence
          </button>
        </div>
      )}
    </>
  );
};

export default AIChatBot;
